import type { TenantConsoleRole } from "./auth";

export type TenantHomepageMode = "default" | "url" | "html";

export interface ActivePlan {
  planCode: string;
  name: string;
}

export interface ActiveSubscription {
  id: number;
  status: string;
  startDate: string;
  endDate: string | null;
}

export interface TenantSettings {
  id: number;
  name: string;
  brandName: string;
  brandNameAlias: string;
  logoLightUrl: string;
  logoDarkUrl: string;
  faviconUrl: string;
  supportProvider: "none" | "wechat";
  supportDisplayName: string;
  supportWechatConfigured: boolean;
  homepageMode: TenantHomepageMode;
  homepageContent: string;
  footerIcpNumber: string;
  footerPsbNumber: string;
  grantedApps: string[];
  enabledApps: string[];
  grantedLoginMethods: string[];
  loginMethods: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  hosts: Array<{
    id: number;
    host: string;
    createdAt: string;
  }>;
}

export interface TeamMember {
  id: number;
  tenantId: number;
  loginName: string;
  displayName: string;
  role: TenantConsoleRole;
  status: "active" | "suspended";
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TenantConsoleAccount {
  id: number;
  tenantId: number;
  loginName: string;
  displayName: string;
  role: TenantConsoleRole;
  status: "active" | "suspended";
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TeamMemberListData {
  accounts: TeamMember[];
  total: number;
  page: number;
  limit: number;
}

export interface TenantUser {
  id: number;
  username: string;
  phone: string | null;
  role: number | null;
  activePlans: ActivePlan[];
  activeSubscription: ActiveSubscription | null;
  boundAt: string;
}

export interface TenantUserListData {
  users: TenantUser[];
  total: number;
  page: number;
  limit: number;
}

export interface AuditLogItem {
  id: number;
  operatorId: number | null;
  operatorDisplayName: string;
  action: string;
  targetType: string;
  targetId: number | null;
  detail: string;
  createdAt: string;
}

export interface AuditLogListData {
  logs: AuditLogItem[];
  total: number;
  page: number;
  limit: number;
}

export interface DashboardStats {
  totalUsers: number;
  newUsersThisMonth: number;
  activeSubscribedUsers: number;
  revenueThisMonth: number;
}

export interface DashboardUserGrowthItem {
  date: string;
  count: number;
}

export interface DashboardRevenueItem {
  month: string;
  revenue: number;
}

export interface DashboardOverview {
  stats: DashboardStats;
  trends: {
    userGrowth30d: DashboardUserGrowthItem[];
    revenue6m: DashboardRevenueItem[];
  };
}

export interface TenantCommissionSummary {
  totalCommission: number;
  totalOrders: number;
  pendingCommission: number;
  settledCommission: number;
  refundedCommission: number;
}

export type TenantOrderPaymentStatus = "PAID" | "REFUNDED";
export type TenantOrderCommissionStatus = "PENDING" | "SETTLED" | "REFUNDED";

export interface TenantOrderRecord {
  subscriptionId: number;
  userId: number;
  username: string;
  planName: string;
  pricingName: string | null;
  orderAmount: number;
  settlementAmount: number | null;
  commissionAmount: number | null;
  paymentStatus: TenantOrderPaymentStatus;
  commissionStatus: TenantOrderCommissionStatus | null;
  paymentTime: string | null;
  refundedAt: string | null;
  createdAt: string;
}

export interface TenantOrderRecordListData {
  records: TenantOrderRecord[];
  total: number;
  page: number;
  limit: number;
}

export interface TenantSettingsPayload {
  name?: string;
  brandName?: string;
  brandNameAlias?: string;
  logoLightUrl?: string;
  logoDarkUrl?: string;
  supportProvider?: "none" | "wechat";
  supportDisplayName?: string;
  supportWechatToken?: string;
  homepageMode?: TenantHomepageMode;
  homepageContent?: string;
  footerIcpNumber?: string;
  footerPsbNumber?: string;
  enabledApps?: string[];
  loginMethods?: string[];
}

export interface TeamMemberPayload {
  loginName: string;
  displayName: string;
  password: string;
}

export interface TeamMemberPasswordResetPayload {
  password: string;
}

/**
 * 所有者修改自己密码请求
 */
export interface OwnerPasswordChangePayload {
  oldPassword: string;
  newPassword: string;
}

export interface TenantLogoUploadResponse {
  variant: "light" | "dark";
  logoUrl: string;
  previewUrl: string;
}

export interface TenantFaviconUploadResponse {
  faviconUrl: string;
  previewUrl: string;
}

export interface TenantSupportLinkResponse {
  provider: "none" | "wechat";
  url: string;
}

/**
 * 租户套餐销售配置合并项
 */
export interface TenantPricingMergedItem {
  pricingId: number;
  planId: number;
  planName: string | null;
  pricingName: string | null;
  durationDays: number;
  durationType: string;
  pricingPrice: number;
  pricingOriginalPrice: number | null;
  settlementPrice: number;
  configId: number | null;
  customPrice: number | null;
  isVisible: boolean;
  displayOrder: number;
}

/**
 * 租户套餐销售配置合并列表数据
 */
export interface TenantPricingMergedListData {
  items: TenantPricingMergedItem[];
  total: number;
}

/**
 * 租户套餐销售配置 upsert 请求
 */
export interface TenantPricingUpsertPayload {
  customPrice?: number | null;
  isVisible?: boolean;
  displayOrder?: number;
}

export interface NavigationItem {
  label: string;
  path: string;
  ownerOnly?: boolean;
}

export const ROLE_LABELS: Record<number, string> = {
  0: "普通用户",
  1: "超级管理员",
  2: "管理员",
  3: "教师",
};

export const TENANT_CONSOLE_ROLE_LABELS: Record<TenantConsoleRole, string> = {
  owner: "租户所有者",
  operator: "租户运营者",
};

export const APP_OPTIONS = [
  { label: "Mock 模考", value: "mock" },
  { label: "AI 批改", value: "aic" },
];

export const LOGIN_METHOD_OPTIONS = [
  { label: "扫码登录", value: "qr" },
  { label: "短信验证码", value: "sms" },
  { label: "密码登录", value: "password" },
  { label: "Google 登录", value: "google" },
];
